import { useAuth } from '@core/context/AuthContext';
import { useInventoryState } from '@core/context/InventoryContext';
import { 
    collection, 
    onSnapshot, 
    query, 
    orderBy, 
    addDoc, 
    deleteDoc, 
    doc, 
    updateDoc, 
    serverTimestamp,
    db 
} from '@core/firebase';
import { useState, useEffect, useCallback } from 'react';

/**
 * useLocations - Hook to manage the official Workshop Zones/Areas catalog.
 */
export const useLocations = () => {
    const [locations, setLocations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { user } = useAuth();
    const { products } = useInventoryState();

    useEffect(() => {
        const q = query(collection(db, 'locations'), orderBy('name', 'asc'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            setLocations(snapshot.docs.map(doc => ({ 
                id: doc.id, 
                ...doc.data() 
            })));
            setLoading(false);
        }, (err) => {
            console.error("Error fetching locations:", err);
            setError(err.message);
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    const addLocation = useCallback(async (name, description = '') => {
        if (!name?.trim()) throw new Error("Zone name is required");
        
        const exists = locations.some(l => l.name.toLowerCase() === name.trim().toLowerCase());
        if (exists) throw new Error("This zone already exists");

        const normalizedName = name.trim();
        const docRef = await addDoc(collection(db, 'locations'), {
            name: normalizedName,
            description: description.trim(),
            isActive: true,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
        });

        await addDoc(collection(db, 'logs'), {
            timestamp: serverTimestamp(),
            product_sku: 'CATALOG_ZONE',
            product_name: normalizedName,
            product_description: `New Zone: ${normalizedName}`,
            type: 'STRUCTURAL_CHANGE',
            amount: 0,
            user_email: user?.email || 'system@inventory.com',
            user_name: user?.displayName || user?.email || 'System',
            note: `Zone ${normalizedName} created`
        });

        return docRef.id;
    }, [locations, user]);

    const updateLocation = useCallback(async (id, newName, newDescription = '') => {
        if (!newName?.trim()) throw new Error("Name is required");
        const docRef = doc(db, 'locations', id);
        const normalizedName = newName.trim();

        await updateDoc(docRef, {
            name: normalizedName,
            description: newDescription.trim(),
            updatedAt: serverTimestamp()
        });

        await addDoc(collection(db, 'logs'), {
            timestamp: serverTimestamp(),
            product_sku: 'CATALOG_ZONE',
            product_name: normalizedName,
            product_description: `Updated Zone: ${normalizedName}`,
            type: 'STRUCTURAL_CHANGE',
            amount: 0,
            user_email: user?.email || 'system@inventory.com',
            user_name: user?.displayName || user?.email || 'System',
            note: `Zone metadata updated`
        });
    }, [user]);

    const deleteLocation = useCallback(async (id, options = {}) => {
        const target = locations.find(l => l.id === id);
        if (!target) throw new Error("Zone not found");

        const { force = false } = options;
        if (!force) {
            const hasItems = products.some(p => p.location?.section === target.name);
            if (hasItems) {
                throw new Error(`Cannot delete "${target.name}": This zone contains active items.`);
            }
        }

        await deleteDoc(doc(db, 'locations', id));

        await addDoc(collection(db, 'logs'), {
            timestamp: serverTimestamp(),
            product_sku: 'CATALOG_ZONE',
            product_name: target.name,
            product_description: `Deleted Zone: ${target.name}`,
            type: 'STRUCTURAL_CHANGE',
            amount: 0,
            user_email: user?.email || 'system@inventory.com',
            user_name: user?.displayName || user?.email || 'System',
            note: `Zone removed from system`
        });
    }, [locations, products, user]);

    const toggleLocationStatus = useCallback(async (id, currentStatus) => {
        const target = locations.find(l => l.id === id);
        if (!target) return;

        const newStatus = !currentStatus;
        await updateDoc(doc(db, 'locations', id), {
            isActive: newStatus,
            updatedAt: serverTimestamp()
        });

        await addDoc(collection(db, 'logs'), {
            timestamp: serverTimestamp(),
            product_sku: 'CATALOG_ZONE',
            product_name: target.name,
            product_description: `${newStatus ? 'Activated' : 'Deactivated'} Zone: ${target.name}`,
            type: 'STRUCTURAL_CHANGE',
            amount: 0,
            user_email: user?.email || 'system@inventory.com',
            user_name: user?.displayName || user?.email || 'System',
            note: `Zone ${newStatus ? 'activated' : 'deactivated'} in system`
        });
    }, [locations, user]);

    const syncFromProducts = useCallback(async (productLocations) => {
        const unique = [...new Set(productLocations)].filter(Boolean);
        let count = 0;
        for (const name of unique) {
            const exists = locations.some(l => l.name.toLowerCase() === name.toLowerCase());
            if (!exists) {
                await addDoc(collection(db, 'locations'), {
                    name,
                    createdAt: serverTimestamp(),
                    updatedAt: serverTimestamp()
                });
                count++;
            }
        }
        return count;
    }, [locations]);

    return { 
        locations, 
        loading, 
        error, 
        addLocation, 
        updateLocation, 
        deleteLocation,
        toggleLocationStatus,
        syncFromProducts
    };
};
